import type { AiState, ApplicationStatus, JobStatus } from "@cv-screener/shared";
import { AlertTriangle, ArrowLeft, ArrowRight, ArrowUpRight, Check, ChevronDown, LoaderCircle, Search, X } from "lucide-react";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function PageHeader({ eyebrow, title, description, details, actions }: { eyebrow: string; title: string; description?: ReactNode; details?: ReactNode; actions?: ReactNode }) {
  void description;
  return <header className="page-header"><div><p className="eyebrow">{eyebrow}</p><h1>{title}</h1>{details}</div>{actions && <div className="header-actions">{actions}</div>}</header>;
}

export function Badge({ children, tone = "neutral" }: { children: ReactNode; tone?: "neutral" | "good" | "info" | "warn" | "danger" | "violet" }) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function StatusBadge({ status }: { status: ApplicationStatus | JobStatus | AiState | string }) {
  const tone = ["Open", "Completed"].includes(status) ? "good" : ["Reviewed", "Invited for Interview", "Interviewed", "Shortlisted", "Offer", "Processing"].includes(status) ? "info" : ["Paused", "Outdated", "New", "Pending"].includes(status) ? "warn" : ["Rejected", "Failed"].includes(status) ? "danger" : "neutral";
  return <Badge tone={tone}>{status}</Badge>;
}

export function Score({ value, compact = false }: { value: number; compact?: boolean }) {
  const tone = value >= 85 ? "score-high" : value >= 70 ? "score-mid" : value >= 50 ? "score-low" : "score-risk";
  return <span className={`score ${tone} ${compact ? "compact" : ""}`}><strong>{value.toFixed(1)}</strong>{!compact && <small>/100</small>}</span>;
}

export function EmptyState({ title, detail, icon }: { title: string; detail: string; icon?: ReactNode }) {
  return <div className="empty-state">{icon}<h3>{title}</h3><p>{detail}</p></div>;
}

export function Loading({ label = "Loading main page" }: { label?: string }) {
  return <div className="loading-state"><LoaderCircle className="spin" size={22} /><span>{label}</span></div>;
}

export function ErrorState({ error }: { error: unknown }) {
  const code = error && typeof error === "object" && "code" in error ? String(error.code) : "";
  if (code.startsWith("SHAREPOINT_") || code === "INVALID_SHAREPOINT_JSON") {
    return <div className="dependency-state"><AlertTriangle size={20} /><div><strong>SharePoint is temporarily unavailable</strong><p>This document-backed feature is limited, but the rest of the workspace remains available.</p></div></div>;
  }
  return <div className="error-state"><AlertTriangle size={20} /><div><strong>Could not load this view</strong><p>{error instanceof Error ? error.message : "Try again or contact an administrator."}</p></div></div>;
}

export function DependencyNotice({ areas }: { areas: string[] }) {
  void areas;
  return null;
}

export function SearchField({ value, onChange, placeholder = "Search" }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search-field filter-control"><Search size={17} /><input aria-label={placeholder} value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} /></label>;
}

export function MultiSelect({ label, options, value, onChange, className = "" }: { label: string; options: Array<{ value: string; label: string }>; value: string[]; onChange: (values: string[]) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (event: MouseEvent) => { if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", escape); };
  }, []);
  const selectedLabels = options.filter((option) => value.includes(option.value) && option.value !== "all").map((option) => option.label);
  const summary = value.includes("all") || !selectedLabels.length ? options[0]?.label ?? label : selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selected`;
  const toggle = (optionValue: string) => {
    if (optionValue === "all") { onChange(["all"]); return; }
    const selected = value.filter((item) => item !== "all");
    const next = selected.includes(optionValue) ? selected.filter((item) => item !== optionValue) : [...selected, optionValue];
    onChange(next.length ? next : ["all"]);
  };
  return <div className={`multi-filter filter-control ${className}`} ref={ref}><span className="multi-filter-label">{label}</span><button type="button" className="multi-filter-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((current) => !current)}><span>{summary}</span><ChevronDown size={14} /></button>{open && <div className="multi-filter-menu" role="listbox" aria-label={label} aria-multiselectable="true">{options.map((option) => { const checked = value.includes(option.value); return <button type="button" role="option" aria-selected={checked} className={`multi-filter-option${checked ? " selected" : ""}`} key={option.value} onClick={() => toggle(option.value)}><span className="multi-filter-check">{checked && <Check size={12} />}</span>{option.label}</button>; })}</div>}</div>;
}

export const pageSizeOptions = [10, 20, 50, 100] as const;

function pageNumbers(page: number, pageCount: number): Array<number | "ellipsis-start" | "ellipsis-end"> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((value) => pages.add(value));
  if (page >= pageCount - 2) [pageCount - 3, pageCount - 2, pageCount - 1].forEach((value) => pages.add(value));
  const ordered = [...pages].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b);
  const result: Array<number | "ellipsis-start" | "ellipsis-end"> = [];
  ordered.forEach((value, index) => {
    const previous = ordered[index - 1];
    if (previous !== undefined && value - previous > 1) result.push(index === 1 ? "ellipsis-start" : "ellipsis-end");
    result.push(value);
  });
  return result;
}

export function PaginationControls({ page, pageSize, total, onPageChange, onPageSizeChange, sizes = pageSizeOptions, itemLabel = "Rows" }: { page: number; pageSize: number; total: number; onPageChange: (page: number) => void; onPageSizeChange: (pageSize: number) => void; sizes?: readonly number[]; itemLabel?: string }) {
  if (!total) return null;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);
  return <div className="pagination-bar">
    <label className="pagination-size">{itemLabel} per page<select aria-label={`${itemLabel} per page`} value={pageSize} onChange={(event) => onPageSizeChange(Number(event.target.value))}>{sizes.map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
    <nav className="pagination-pages" aria-label="Pagination">
      <button type="button" className="pagination-arrow" aria-label="Previous page" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}><ArrowLeft size={14} /></button>
      {pageNumbers(currentPage, pageCount).map((item) => item === "ellipsis-start" || item === "ellipsis-end" ? <span className="pagination-ellipsis" key={item}>…</span> : <button type="button" className={`pagination-page${item === currentPage ? " active" : ""}`} aria-current={item === currentPage ? "page" : undefined} aria-label={`Page ${item}`} key={item} onClick={() => onPageChange(item)}>{item}</button>)}
      <button type="button" className="pagination-arrow" aria-label="Next page" disabled={currentPage === pageCount} onClick={() => onPageChange(currentPage + 1)}><ArrowRight size={14} /></button>
    </nav>
    <span className="pagination-summary">{start}–{end} of {total}</span>
  </div>;
}

export function ProgressBar({ value, tone }: { value: number; tone?: "good" | "warn" | "danger" }) {
  const resolved = tone ?? (value < 60 ? "good" : value < 85 ? "warn" : "danger");
  const boundedValue = Math.min(100, Math.max(0, value));
  return <div className="progress-track" role="progressbar" aria-label="AI request limit used" aria-valuemin={0} aria-valuemax={100} aria-valuenow={boundedValue}><span className={resolved} style={{ width: `${boundedValue}%` }} /></div>;
}

export function Dialog({ open, title, children, onClose, actions }: { open: boolean; title: string; children: ReactNode; onClose: () => void; actions: ReactNode }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusableSelector = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";
    const focusFirst = window.requestAnimationFrame(() => dialogRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = [...dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector)].filter((element) => !element.hidden);
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable.at(-1)!;
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFirst);
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [open]);
  if (!open) return null;
  return <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}><div className="dialog" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}><div className="dialog-head"><h2 id={titleId}>{title}</h2><button className="icon-button" onClick={onClose} aria-label="Close"><X size={19} /></button></div><div className="dialog-body">{children}</div><div className="dialog-actions">{actions}</div></div></div>;
}

export function Toast({ message, tone = "success", onClose }: { message: string; tone?: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const timer = window.setTimeout(onClose, 3500); return () => window.clearTimeout(timer); }, [onClose]);
  return <div className={`toast ${tone}`} role={tone === "error" ? "alert" : "status"} aria-live={tone === "error" ? "assertive" : "polite"} aria-atomic="true">{tone === "success" ? <Check size={18} /> : <AlertTriangle size={18} />}<span>{message}</span><button onClick={onClose} aria-label="Dismiss notification"><X size={15} /></button></div>;
}

export function ExternalLinkButton({ href, children }: { href: string; children: ReactNode }) {
  return <a className="button secondary" href={href} target="_blank" rel="noreferrer">{children}<ArrowUpRight size={16} /></a>;
}
